import type { SupabaseClient } from '../client'
import { SupabaseError } from '../errors'
import { unwrapList } from './_helpers'

/**
 * O chamado da garantia, do ponto de vista do cliente.
 *
 * A tabela é `operacoes.suporte_ajustes`, que o ADR-078 transformou na face do
 * cliente: o trabalho mora em `operacoes.tasks`, aqui fica o que ele vê.
 * A RLS (`suporte_ajustes_client_read`, migration 20260724003630) já garante
 * que só chega o que o time liberou (`cliente_visivel`) nas iniciativas dele.
 *
 * Os tipos gerados não cobrem o schema `operacoes` para o portal, então as
 * linhas são tipadas à mão aqui, como já se faz em `types/conhecimento.ts`.
 */

/** Situação do chamado. Os rótulos de cliente ficam na UI, não aqui. */
export type ChamadoStatus =
  | 'aberto'
  | 'em_desenvolvimento'
  | 'em_qa'
  | 'entregue'
  | 'fora_de_escopo'

/** Triagem do time: o que a garantia cobre e o que vira aditivo. */
export type ChamadoTipo = 'defeito' | 'duvida' | 'fora_escopo'

/** Quem carimbou o aceite. A coluna existe para nunca confundir os três. */
export type AceiteOrigem = 'cliente' | 'tacito' | 'time'

export type ChamadoRow = {
  id: string
  initiative_id: number
  title: string
  description: string | null
  status: ChamadoStatus
  prioridade: 'baixa' | 'normal' | 'critica'
  tipo: ChamadoTipo | null
  cliente_visivel: boolean
  responsavel_id: string | null
  /** A previsão de entrega que o time preenche no ERP. O cliente lê esta data. */
  prazo_at: string | null
  resposta_time: string | null
  respondido_at: string | null
  entregue_at: string | null
  aceite_cliente_at: string | null
  aceite_origem: AceiteOrigem | null
  aceite_por: string | null
  aceite_prazo_at: string | null
  bug_report_id: number | null
  created_at: string
  updated_at: string
}

/**
 * As colunas que o cliente enxerga.
 *
 * O hook antigo lia só metade: sem `tipo`, sem `bug_report_id`, e sem nenhuma
 * das quatro colunas de aceite que a migration 20260814091000 criou. Sem elas,
 * o portal não sabe dizer se um chamado espera resposta dele nem até quando.
 *
 * `prazo_at` entrou na lista com o ADR-085: é a previsão de entrega, e o
 * cliente passou a vê-la na ficha. Antes disso o time contornava escrevendo
 * "Prazo: 07/08" dentro de `resposta_time`, como texto solto.
 *
 * Fora da lista de propósito: `task_id`, `aditivo_id` e `origem_demanda_id`.
 * São leitura de equipe, e `origem_demanda_id` está depreciada desde o ADR-078.
 */
const CHAMADO_COLUMNS =
  'id, initiative_id, title, description, status, prioridade, tipo, cliente_visivel, ' +
  'responsavel_id, prazo_at, resposta_time, respondido_at, entregue_at, ' +
  'aceite_cliente_at, aceite_origem, aceite_por, aceite_prazo_at, ' +
  'bug_report_id, created_at, updated_at'

/** Os chamados de uma iniciativa que o time liberou para o cliente. */
export async function listChamadosByInitiative(
  client: SupabaseClient,
  initiativeId: number,
): Promise<ChamadoRow[]> {
  const query = client
    .schema('operacoes')
    .from('suporte_ajustes')
    .select(CHAMADO_COLUMNS)
    .eq('initiative_id', initiativeId)
    .order('created_at', { ascending: false })

  return unwrapList(await query) as unknown as ChamadoRow[]
}

export type JanelaGarantiaRow = {
  start_date: string | null
  target_date: string | null
  delivered_at: string | null
}

/**
 * A janela de garantia da iniciativa, lida do bloco `tipo='garantia'`.
 *
 * O portal calculava `delivered_at + 30` no JS. Desde que a janela virou
 * editável (migration 20260814120000), esse cálculo passou a divergir do que o
 * time vê. Quem transforma isto em dias é `getGarantiaWindow`, no portal.
 */
export async function getJanelaGarantia(
  client: SupabaseClient,
  initiativeId: number,
): Promise<JanelaGarantiaRow | null> {
  const portal = client.schema('portal')
  const { data, error } = await portal.rpc('get_janela_garantia', {
    p_initiative_id: initiativeId,
  })

  if (error) throw new SupabaseError(error.message, error)
  const linhas = data ?? []
  return linhas[0] ?? null
}

/**
 * O aceite do cliente.
 *
 * Passa pela RPC de propósito: ela é o único caminho que escreve
 * `aceite_origem='cliente'`, e é lá que moram as travas que a tela sozinha não
 * garante (só chamado entregue, só chamado liberado, e nunca reescrever um
 * chamado já fechado). Um UPDATE direto daqui furaria todas elas.
 */
export async function aceitarChamado(
  client: SupabaseClient,
  chamadoId: string,
): Promise<ChamadoRow> {
  const portal = client.schema('portal')
  const { data, error } = await portal.rpc('aceitar_ajuste_suporte', {
    p_ajuste_id: chamadoId,
  })

  if (error) throw new SupabaseError(error.message, error)
  // O banco devolve `status` como texto solto: a coluna é text com CHECK, não
  // enum, então o tipo gerado não carrega a união. Quem garante os valores é o
  // CHECK, e o estreitamento aqui é a tradução disso, não uma suposição.
  return data as ChamadoRow
}

/** Uma fala no chamado: do cliente ou de quem cuida dele. */
export type MensagemChamado = {
  id: string
  texto: string
  efeito: 'recusa' | null
  /** Paths no bucket de prints, no mesmo formato do relato. */
  anexos: string[]
  created_at: string
  autor_nome: string
  autor_e_time: boolean
}

/**
 * A conversa do chamado.
 *
 * Nasceu porque o cliente precisa justificar a recusa por escrito (fase P6):
 * sem isso o chamado voltava para a fila e o time não sabia o que faltou.
 */
export async function listarMensagensChamado(
  client: SupabaseClient,
  chamadoId: string,
): Promise<MensagemChamado[]> {
  const portal = client.schema('portal')
  const { data, error } = await portal.rpc('listar_mensagens_chamado', {
    p_ajuste_id: chamadoId,
  })

  if (error) throw new SupabaseError(error.message, error)
  // `efeito` chega como texto solto pelo mesmo motivo: o CHECK da coluna é que
  // garante os valores, e o tipo gerado não carrega a união
  return (data ?? []) as MensagemChamado[]
}

/**
 * A recusa do cliente, com motivo obrigatório.
 *
 * Passa pela RPC porque ela grava a mensagem e reabre o chamado numa transação
 * só, e porque é lá que mora a trava do prazo definitivo: chamado fechado não
 * reabre, nem pelo aceite do cliente nem pelo tácito.
 */
export async function recusarChamado(
  client: SupabaseClient,
  chamadoId: string,
  texto: string,
): Promise<ChamadoRow> {
  const portal = client.schema('portal')
  const { data, error } = await portal.rpc('recusar_ajuste_suporte', {
    p_ajuste_id: chamadoId,
    p_texto: texto,
  })

  if (error) throw new SupabaseError(error.message, error)
  // mesmo estreitamento do aceite: `status` é text com CHECK, não enum
  return data as ChamadoRow
}

/**
 * As RPCs do ADR-085 são mais novas que `database.types.ts`.
 *
 * O mesmo caminho que `createBugReport` já usa para `expected_behavior`: o cast
 * fica num lugar só, com o nome da migration ao lado, em vez de espalhar
 * `as never` por cada chamada. A assinatura de verdade está em
 * `20260911140000_adr085_ficha_do_chamado.sql`.
 */
type RespostaRpc = { error: { message: string } | null }

function chamarRpc(
  client: SupabaseClient,
  nome: string,
  args: Record<string, unknown>,
): Promise<RespostaRpc> {
  const portal = client.schema('portal') as unknown as {
    rpc: (nome: string, args: Record<string, unknown>) => Promise<RespostaRpc>
  }
  return portal.rpc(nome, args)
}

/** Uma transição do chamado, com hora. Alimenta a linha do tempo e a jornada. */
export type EventoChamado = {
  id: string
  tipo: 'criado' | 'status' | 'prazo' | 'responsavel' | 'aceite'
  de: string | null
  para: string | null
  por_usuario: string | null
  por_cliente: string | null
  created_at: string
}

/**
 * O que já aconteceu com o chamado.
 *
 * A tabela nasceu com o ADR-085: sem transição registrada não existe duração, e
 * é a duração que faz a jornada dizer alguma coisa ("está aqui há 9 dias").
 * Chamado antigo só tem o que deu para reconstruir de coluna com hora, então a
 * lista pode vir curta, e a tela não completa o que ninguém registrou.
 */
export async function listarEventosChamado(
  client: SupabaseClient,
  chamadoId: string,
): Promise<EventoChamado[]> {
  // a tabela é mais nova que `database.types.ts`, então a consulta passa pelo
  // mesmo cast das RPCs. A RLS é quem filtra: só chegam eventos de chamado que
  // esta pessoa já enxerga.
  const operacoes = client.schema('operacoes') as unknown as {
    from: (tabela: string) => {
      select: (colunas: string) => {
        eq: (
          coluna: string,
          valor: string,
        ) => {
          order: (
            coluna: string,
            opts: { ascending: boolean },
          ) => Promise<{ data: EventoChamado[] | null; error: { message: string } | null }>
        }
      }
    }
  }

  const { data, error } = await operacoes
    .from('suporte_ajustes_eventos')
    .select('id, tipo, de, para, por_usuario, por_cliente, created_at')
    .eq('ajuste_id', chamadoId)
    .order('created_at', { ascending: true })

  if (error) throw new SupabaseError(error.message, error)
  return data ?? []
}

/**
 * A fala do cliente no chamado.
 *
 * Passa pela RPC pelo mesmo motivo do aceite e da recusa: o portal é read-only
 * sobre `operacoes`, e a trava de "chamado fechado é somente leitura" precisa
 * valer no banco, não só na tela. Comentar não muda status e não reabre.
 */
export async function escreverNoChamado(
  client: SupabaseClient,
  chamadoId: string,
  texto: string,
  anexos: string[] = [],
): Promise<void> {
  const { error } = await chamarRpc(client, 'escrever_no_chamado', {
    p_ajuste_id: chamadoId,
    p_texto: texto,
    p_anexos: anexos,
  })

  if (error) throw new SupabaseError(error.message, error)
}

/** A resposta da equipe, do lado do ERP. Mesma conversa, outro autor. */
export async function responderChamado(
  client: SupabaseClient,
  chamadoId: string,
  texto: string,
): Promise<void> {
  const { error } = await chamarRpc(client, 'responder_chamado', {
    p_ajuste_id: chamadoId,
    p_texto: texto,
  })

  if (error) throw new SupabaseError(error.message, error)
}

const ANEXOS_BUCKET = 'bug-screenshots'

/**
 * Sobe um print da conversa e devolve o path.
 *
 * Mesmo bucket do relato: para quem lê, print de abertura e print de conversa
 * são a mesma coisa, e dois buckets seriam duas políticas para o mesmo arquivo.
 */
export async function subirAnexoDoChamado(
  client: SupabaseClient,
  projectId: number,
  file: File,
): Promise<string> {
  const ext = file.name.split('.').pop() ?? 'bin'
  const path = `${projectId}/conversa/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`
  const { error } = await client.storage.from(ANEXOS_BUCKET).upload(path, file)
  if (error) throw new SupabaseError(error.message, error)
  return path
}

/** URL pública de um print. Aceita path do storage ou URL completa. */
export function urlDoAnexo(client: SupabaseClient, path: string | null): string | null {
  if (!path) return null
  if (path.startsWith('http')) return path
  return client.storage.from(ANEXOS_BUCKET).getPublicUrl(path).data.publicUrl
}
